import React from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { LocationDetailList, StateColumns } from '@/components/locations/LocationIndex';
import type { LocationHubData } from '@/lib/locations';
import {
  LOCATIONS_PATH,
  groupByPlace,
  hubDescription,
  hubHeading,
  hubIntro,
  hubTitle,
} from '@/utils/locationHubs';

const BASE_URL = 'https://www.milkywayplanner.com';

/**
 * A country or US state hub: links to every dark sky location in it
 * (ERI-2853). The route loader builds it from the committed snapshot, so the
 * links are in the prerendered HTML.
 */
export default function LocationHub() {
  const { hub } = useLoaderData() as LocationHubData;

  if (!hub) {
    return (
      <>
        <SEOHead title="Place Not Found | Milky Way Planner" noindex />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-4">
            <h1 className="font-serif text-2xl font-bold text-foreground">No dark sky locations here</h1>
            <p className="text-muted-foreground">We have no dark sky locations listed for this place, or the link is incorrect.</p>
            <Link to={LOCATIONS_PATH}>
              <Button variant="outline" className="gap-2 rounded-[4px]">
                <ArrowLeft className="w-4 h-4" /> Back to Locations
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  // The US hub lists its locations under their states.
  const country = hub.kind === 'country' ? groupByPlace(hub.locations)[0] : undefined;
  const byState = country && country.states.length > 0 ? country : undefined;

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { name: 'Dark Sky Locations', path: LOCATIONS_PATH },
      ...hub.trail,
    ].map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${BASE_URL}${item.path}`,
    })),
  };

  return (
    <>
      <SEOHead
        title={hubTitle(hub)}
        description={hubDescription(hub)}
        canonicalUrl={hub.path}
        structuredData={breadcrumb}
      />
      <div className="min-h-screen bg-background">
        <section className="bg-[#0f1923] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <nav aria-label="Breadcrumb" className="mb-6 text-sm text-white/60">
              <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <li>
                  <Link to={LOCATIONS_PATH} className="hover:text-gold transition-colors">Dark Sky Locations</Link>
                </li>
                {hub.trail.map((item, i) => (
                  <li key={item.path} className="flex items-center gap-2">
                    <span aria-hidden="true">/</span>
                    {i === hub.trail.length - 1 ? (
                      <span aria-current="page" className="text-white/80">{item.name}</span>
                    ) : (
                      <Link to={item.path} className="hover:text-gold transition-colors">{item.name}</Link>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-4 leading-[1.1]">
              {hubHeading(hub)}
            </h1>
            <p className="text-white/70 text-lg max-w-3xl leading-relaxed">
              {hubIntro(hub)} Each page has the Bortle class, the best months for the Milky Way and a free visibility calendar.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          {byState ? (
            <StateColumns states={byState.states} unplaced={byState.unplaced} headingLevel="h2" />
          ) : (
            <LocationDetailList locations={hub.locations} />
          )}

          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-border pt-6 text-sm">
            <Link to={LOCATIONS_PATH} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" /> All dark sky locations
            </Link>
            <Link to="/planner" className="inline-flex items-center gap-1.5 font-semibold text-[#c9922a] hover:underline">
              Check any spot in the planner <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
